import { forwardRef } from 'react';

import {
  Badge,
  MaterialShapeAngleIcon,
  MaterialShapePlateIcon,
  MaterialShapeRoundRodIcon,
  MaterialShapeIBeamIcon,
  MaterialShapeChannelIcon,
  MaterialShapeHexBarIcon,
  MaterialShapePipeIcon,
  MaterialShapeRectBarIcon,
  MaterialShapeRectTubeIcon,
  MaterialShapeRoundBarIcon,
  MaterialShapeRoundTubeIcon,
  MaterialShapeSheetIcon,
  MaterialShapeSquareBarIcon,
  MaterialShapeSquareTubeIcon,
  type BadgeProps,
  BadgeSize,
  Center,
  TruncateTextWithTooltip,
  BadgePaperlessColor,
} from '@frontend/components';

import { type UserGroupBadgeIcon } from './const';

const HEIGHT_MAP = {
  xs: 2.5,
  sm: 3,
  md: 3.5,
  lg: 4,
} as const satisfies Record<BadgeSize, number>;

const GAP_MAP = {
  xs: 0.5,
  sm: 0.5,
  md: 0.75,
  lg: 0.75,
} as const satisfies Record<BadgeSize, number>;

const getIcon = (icon: UserGroupBadgeIcon) => {
  const iconProps = {
    boxSize: '100%',
    color: 'feature.onInteraction',
    secondaryColor: 'rgba(255,255,255,0.3)',
  };
  switch (icon) {
    case 'angle':
      return <MaterialShapeAngleIcon {...iconProps} />;
    case 'i_beam':
      return <MaterialShapeIBeamIcon {...iconProps} />;
    case 'channel':
      return <MaterialShapeChannelIcon {...iconProps} />;
    case 'hex_bar':
      return <MaterialShapeHexBarIcon {...iconProps} />;
    case 'pipe':
      return <MaterialShapePipeIcon {...iconProps} />;
    case 'plate':
      return <MaterialShapePlateIcon {...iconProps} />;
    case 'rect_bar':
      return <MaterialShapeRectBarIcon {...iconProps} />;
    case 'rect_tube':
      return <MaterialShapeRectTubeIcon {...iconProps} />;
    case 'round_bar':
      return <MaterialShapeRoundBarIcon {...iconProps} />;
    case 'round_rod':
      return <MaterialShapeRoundRodIcon {...iconProps} />;
    case 'round_tube':
      return <MaterialShapeRoundTubeIcon {...iconProps} />;
    case 'sheet':
      return <MaterialShapeSheetIcon {...iconProps} />;
    case 'square_bar':
      return <MaterialShapeSquareBarIcon {...iconProps} />;
    case 'square_tube':
      return <MaterialShapeSquareTubeIcon {...iconProps} />;
    default:
      return null;
  }
};

export type UserGroupBadgeProps = {
  name: string;
  colorPalette: BadgePaperlessColor;
  icon: UserGroupBadgeIcon;
} & Omit<BadgeProps, 'variant' | 'colorScheme'>;

export const UserGroupBadge = forwardRef<'span', UserGroupBadgeProps>(
  ({ name, colorPalette, icon, ...badgeProps }, ref) => {
    const iconElement = getIcon(icon);
    const height = HEIGHT_MAP[badgeProps.size as BadgeSize] ?? HEIGHT_MAP.sm;
    const gap = GAP_MAP[badgeProps.size as BadgeSize] ?? GAP_MAP.sm;

    return (
      <Badge
        ref={ref}
        variant="subtle"
        colorScheme={colorPalette}
        textTransform="capitalize"
        pl={0}
        pr={gap}
        display="inline-flex"
        alignItems="center"
        gap={gap}
        height={height}
        {...badgeProps}
      >
        <Center
          as="span"
          my="-1px"
          ml="-1px"
          bg={`${colorPalette}.500`}
          height="calc(100% + 2px)"
          aspectRatio="1/1"
          borderLeftRadius="sm"
        >
          {iconElement}
        </Center>
        <TruncateTextWithTooltip
          color="inherit"
          lineHeight="inherit"
          fontWeight="inherit"
          fontFamily="inherit"
          fontSize="inherit"
          as="span"
          flex={1}
          minWidth={0}
          text={name}
          tooltipProps={{ variant: 'compact' }}
        />
      </Badge>
    );
  },
);

UserGroupBadge.displayName = 'UserGroupBadge';
